import cn from 'classnames'
import Box from 'ext-ui/components/Box/Box'
import Button from 'ext-ui/components/Button/Button'
import Processing from 'ext-ui/components/Processing/Processing'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { useEffect, useState } from 'react'
import {
  useCreateSSOConfiguration,
  useEnableSSO,
} from 'src/api/mutation/groupSsoConfig'
import {
  GroupSSOConfig,
  useGetGroupSsoConfig,
} from 'src/api/query/groupSsoConfig'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import { SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { useQueryOrganizationSaasTier } from 'src/queries/organizations/saas.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

import style from './OrganizationSSOConfig.style'
import OrganizationSSOUpgradeScreen from './OrganizationSSOUpgradeScreen'

const defaultConfigValues: GroupSSOConfig = {
  id: '',
  groupId: '',
  // TODO: Replace hardcoded SAML with OIDC or whatever we want to put in here
  type: 'SAML',
  providerName: '',
  createdAt: '',
  enabled: false,
  samlMetadata: {
    entityId: '',
    acsUrl: '',
    idpSsoUrl: '',
    idpIssuer: '',
    x509CertificatePem: '',
  },
}

const validateConfig = (config: GroupSSOConfig): boolean => {
  if (!config.samlMetadata) {
    return false
  }
  const { acsUrl, entityId, idpIssuer, idpSsoUrl, x509CertificatePem } =
    config.samlMetadata

  if (!acsUrl || !entityId || !idpIssuer || !idpSsoUrl || !x509CertificatePem) {
    return false
  }
  return true
}

export default function OrganizationSSOConfig() {
  const translate = useTranslate()

  const [isEditing, setIsEditing] = useState<boolean>(false)
  /** Holds the config values as if they are being edited */
  const [configValues, setConfigValues] =
    useState<GroupSSOConfig>(defaultConfigValues)

  /** Holds the current config values saved on the backend */
  const [savedConfigValues, setSavedConfigValues] =
    useState<GroupSSOConfig>(defaultConfigValues)

  const [isUpdatingConfig, setIsUpdatingConfig] = useState<boolean>(false)

  const [hasInitiallyFetched, setHasInitiallyFetched] = useState<boolean>(false)

  const { mutateAsync: allowSsoForAllUsers } = useEnableSSO()
  const { mutateAsync: upsertSsoConfig } = useCreateSSOConfiguration()

  const { data, isPending: userFeaturesPending } = useQueryUserFeatures([
    UserFeature.SSO_ENABLED,
  ])
  const orgSsoAllowed = data?.SSO_ENABLED

  const handleFieldChange = (
    name: keyof NonNullable<GroupSSOConfig['samlMetadata']>,
    value: string,
  ) => {
    setConfigValues((prev) => {
      const baseSamlMetadata =
        prev.samlMetadata || defaultConfigValues.samlMetadata!
      return {
        ...prev,
        samlMetadata: {
          ...baseSamlMetadata,
          [name]: value,
        },
      }
    })
  }

  const handleCancel = () => {
    setConfigValues(savedConfigValues)
    setIsEditing(false)
  }

  // Check to see if the user is enterprise
  const { data: currentUser } = useQueryOwnUser({})
  const { data: organizationTier, isFetched: saasTierIsFetched } =
    useQueryOrganizationSaasTier({
      orgId: currentUser?.organizationId,
      enabled: !!currentUser?.organizationId,
    })

  const { whiteLabelIncludes } = useWhitelabel()

  const isEnterprise = organizationTier?.tier === 'ENTERPRISE'
  const isOwnerOrAdmin = currentUser?.hasOrgAdminPermissions
  const isJpm = whiteLabelIncludes(SupportedBrands.Jpm)

  // This endpoint is in on the API side, but the identity service
  // still needs changes to get this working as expected.
  // Keeping this here for when the endpoints are fully working and ready to go
  // so we can uncomment and continue.
  const { data: existingSsoConfig, isPending: groupConfigLoading } =
    useGetGroupSsoConfig(currentUser?.organizationId, {
      enabled: isEnterprise,
    })

  // Handle SSO config response - populate states with fetched data
  useEffect(() => {
    // Only run this effect once on initial fetch
    if (hasInitiallyFetched || !existingSsoConfig) {
      return
    }

    // Populate both states with fetched config data (will be blank if 404)
    setConfigValues(existingSsoConfig)
    setSavedConfigValues(existingSsoConfig)
    setHasInitiallyFetched(true)
  }, [existingSsoConfig, hasInitiallyFetched])

  if (!saasTierIsFetched || groupConfigLoading || userFeaturesPending) {
    return (
      <BackgroundPage>
        <Processing />
      </BackgroundPage>
    )
  }

  // Return not found page if user doesn't have permission or SSO is not enabled
  if (!isOwnerOrAdmin || !orgSsoAllowed) {
    return <NotFoundBackgroundPage />
  }

  // Return upgrade screen if user is ineligible
  if (!isEnterprise && !isJpm) {
    return <OrganizationSSOUpgradeScreen />
  }

  // If the user is editing, check the currently editing values in state
  // otherwise, check the backend saved config values
  const hasValidConfig = isEditing
    ? validateConfig(configValues)
    : validateConfig(savedConfigValues)

  const handleToggleEnable = async (isEnabling: boolean): Promise<boolean> => {
    if (isEditing || !hasValidConfig) {
      return !isEnabling
    }
    // TODO check on this value and do error handling if the response fails.
    // We still are waiting on BE to allow disabling
    const _response = await allowSsoForAllUsers({
      organizationId: savedConfigValues.groupId,
      enabled: isEnabling,
    })
    return isEnabling
  }
  const getSaveButtonEnableState = () => {
    if (isEditing) {
      if (isUpdatingConfig) {
        return isUpdatingConfig
      }
      return !hasValidConfig
    }
    return false
  }

  // Show Configuration Page if all checks pass
  return (
    <BackgroundPage>
      <div
        className={cn('OrganizationSSOConfig', 'flex-column', 'gap-6')}
        css={style}
      >
        <div className="title-bar flex-row justify-between">
          <p className="font-h2">{translate('SSO')}</p>
          <div className="flex-row gap-4 justify-end">
            {isEditing && (
              <Button variant="text" onClick={handleCancel}>
                {'Cancel'}
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={getSaveButtonEnableState()}
              loading={isUpdatingConfig}
              onClick={async () => {
                if (!configValues.samlMetadata) {
                  return
                }
                if (isEditing) {
                  try {
                    // Set Loading state
                    setIsUpdatingConfig(true)
                    const result = await upsertSsoConfig({
                      groupId: configValues.groupId,
                      type: 'SAML',
                      samlAcsUrl: configValues.samlMetadata.acsUrl,
                      samlEntityId: configValues.samlMetadata.entityId,
                      samlIdpIssuer: configValues.samlMetadata.idpIssuer,
                      samlIdpSsoUrl: configValues.samlMetadata.idpSsoUrl,
                      samlX509CertificatePem:
                        configValues.samlMetadata.x509CertificatePem,
                    })
                    setIsUpdatingConfig(false)
                    // Set loading state

                    setIsEditing(false)
                    setSavedConfigValues(result)
                    setConfigValues(result)
                  } catch (error) {
                    setIsUpdatingConfig(false)
                    // TODO: Send a toast here saying something went wrong
                  }
                } else {
                  setIsEditing(true)
                }
              }}
            >
              {translate(isEditing ? 'Save' : 'Edit')}
            </Button>
          </div>
        </div>
        <div className="flex-row gap-5">
          <div className="flex-column box-container">
            <Box>
              <div className="box-content">
                <div className="box-heading flex-column">
                  <p className="font-h3">
                    {translate('Security and access control')}
                  </p>
                  <p className="box-heading-subtitle text-secondary">
                    {translate('Configure additional security requirements')}
                  </p>
                </div>
                <div className="box-item">
                  <div className="box-item-content">
                    <div className="flex-row justify-between align-center">
                      <div className="flex-column">
                        <p className="box-item-title">
                          {translate('Enforce SSO for all users')}
                        </p>
                        <p className="box-item-subtitle">
                          {translate(
                            'Disable email/password login for all organization members',
                          )}
                        </p>
                      </div>
                      <div className="box-item-switch">
                        <OnOffSwitch
                          smallMode
                          disabled={isEditing && hasValidConfig}
                          onChange={handleToggleEnable}
                          defaultOnOffState={configValues.enabled}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </div>
          <div className="flex-column box-container">
            <Box>
              <div className="box-content">
                <div className="box-heading flex-column">
                  <p className="font-h3">{translate('SSO Configuration')}</p>
                  <p className="box-heading-subtitle text-secondary">
                    {translate('Configure your identity provider settings')}
                  </p>
                </div>

                <div className="box-item">
                  <div className="box-item-content">
                    <p className="box-item-title">
                      {translate('Entity ID (Issuer)')}
                    </p>
                    <TextInput
                      name="entityId"
                      value={configValues.samlMetadata?.entityId || ''}
                      onChange={(e) =>
                        handleFieldChange('entityId', e.target.value)
                      }
                      placeholder="https://app.extend.com/saml/metadata"
                      disabled={!isEditing}
                      microcopy={translate(
                        'Use this as the Entity ID in your IdP configuration',
                      )}
                    />
                    <p className="box-item-title with-space">
                      {translate('ACS (Reply) URL')}
                    </p>
                    <TextInput
                      name="acsUrl"
                      value={configValues.samlMetadata?.acsUrl || ''}
                      onChange={(e) =>
                        handleFieldChange('acsUrl', e.target.value)
                      }
                      placeholder="https://app.extend.com/saml/metadata"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                <div className="box-item">
                  <div className="box-item-content">
                    <p className="box-item-title">{translate('IdP SSO URL')}</p>
                    <TextInput
                      name="idpSsoUrl"
                      value={configValues.samlMetadata?.idpSsoUrl || ''}
                      onChange={(e) =>
                        handleFieldChange('idpSsoUrl', e.target.value)
                      }
                      placeholder="https://login.google.com"
                      disabled={!isEditing}
                    />
                    <p className="box-item-title with-space">
                      {translate('IdP Issuer')}
                    </p>
                    <TextInput
                      name="idpIssuer"
                      value={configValues.samlMetadata?.idpIssuer || ''}
                      onChange={(e) =>
                        handleFieldChange('idpIssuer', e.target.value)
                      }
                      placeholder="https://sts.google.net/..."
                      disabled={!isEditing}
                    />
                    <p className="box-item-title with-space">
                      {translate('X.509 Certificate')}
                    </p>
                    <TextAreaInput
                      name="x509CertificatePem"
                      value={
                        configValues.samlMetadata?.x509CertificatePem || ''
                      }
                      onChange={(e) =>
                        handleFieldChange('x509CertificatePem', e.target.value)
                      }
                      placeholder="-----BEGIN CERTIFICATE-----"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>
            </Box>
          </div>
        </div>
      </div>
    </BackgroundPage>
  )
}
